/**
 * @file Review Step Component
 * @description Final step in the integration wizard that displays all collected information
 * for user review before submission. Provides a summary table with masked sensitive data.
 */

import React, { useEffect } from 'react';
import { CircleSpinner } from 'react-spinners-kit';
import { Button, Table } from 'reactstrap';

import { StepComponentProps } from '../StepperModal';
import { CLOUD_PROVIDER_CONFIG, INTEGRATION_MODULE_CONFIG } from '../types';

/**
 * ReviewStep - Displays integration summary for final review
 *
 * This component is the final step in the integration wizard. It serves multiple purposes:
 * 1. Shows all collected information in an organized summary table
 * 2. Masks sensitive credentials for security (access keys, secret keys)
 * 3. Provides clear labels using configuration mappings
 * 4. Allows users to go back and edit before final submission
 * 5. Triggers the integration creation API call on completion
 *
 * Information Displayed:
 * - Cloud Provider (AWS, Azure, GCP, M365)
 * - Integration Module (CSPM, CIEM)
 * - Integration Type (Account Level / Organization Level) - if applicable
 * - Integration Label (friendly name)
 * - Account/Organization ID - if applicable
 * - Access Key (partially masked) - if applicable
 * - Secret Key (fully masked) - if applicable
 *
 * Security Features:
 * - Masks credentials to prevent shoulder surfing
 * - Shows first 4 and last 4 characters of access keys
 * - Fully masks secret keys with asterisks
 *
 * @component
 * @param {StepComponentProps} props - Step component props
 * @returns {React.ReactElement} Summary table with review information
 */
const ReviewStep: React.FC<StepComponentProps> = ({
  description,
  data,
  onValidationChange,
  onNext,
  onBack,
  loading,
  allowBack,
}: StepComponentProps): React.ReactElement => {
  /**
   * Review step is always valid (just displaying information)
   * Notify parent on mount that step is valid
   */
  useEffect(() => {
    onValidationChange(true);
  }, [onValidationChange]);

  /**
   * Handles final submission of the integration
   * Calls onNext which triggers the API call in config.tsx
   */
  const handleComplete = () => {
    onNext(data);
  };

  // Get human-readable labels from configuration objects
  const cloudProviderLabel = data.cloudProvider
    ? CLOUD_PROVIDER_CONFIG[data.cloudProvider].label
    : 'Unknown';
  const integrationModuleLabel = data.integrationModule
    ? INTEGRATION_MODULE_CONFIG[data.integrationModule].fullName
    : 'Unknown';

  /**
   * Build the summary rows as a typed array so <tbody> receives only <tr> elements
   * with no whitespace text nodes or JSX comment nodes between them.
   * Conditional rows are filtered out when the value is not present.
   */
  const summaryRows: { label: string; value: React.ReactNode }[] = [
    { label: 'Cloud Provider', value: <strong>{cloudProviderLabel}</strong> },
    {
      label: 'Integration Module',
      value: <strong>{integrationModuleLabel}</strong>,
    },
    ...(data.integrationType
      ? [
          {
            label: 'Integration Level',
            value: (
              <strong>
                {data.integrationType
                  .replace(/_/g, ' ')
                  .toLowerCase()
                  .replace(/\b\w/g, (c) => c.toUpperCase())}
              </strong>
            ),
          },
        ]
      : []),
    ...(data.integrationLabel
      ? [
          {
            label: 'Integration Name',
            value: <strong>{data.integrationLabel}</strong>,
          },
        ]
      : []),
    ...(data.accountId
      ? [
          {
            label: 'Account/Organization ID',
            value: <strong>{data.accountId}</strong>,
          },
        ]
      : []),
    ...(data.cloudTrailExpiration
      ? [
          {
            label: 'CloudTrail Expiration',
            value: <strong>{data.cloudTrailExpiration}</strong>,
          },
        ]
      : []),
    ...(data.cloudTrailNonCurrent
      ? [
          {
            label: 'CloudTrail Non-Current',
            value: <strong>{data.cloudTrailNonCurrent}</strong>,
          },
        ]
      : []),
    ...(data.athenaResultsExpiration
      ? [
          {
            label: 'Athena Results Expiration',
            value: <strong>{data.athenaResultsExpiration}</strong>,
          },
        ]
      : []),
  ];

  return (
    <div className="step-content-wrapper">
      {/* Zone 1 – Step description (pinned header) */}
      <div className="step-description-header">
        <h5>{description ?? 'Review and Submit'}</h5>
      </div>

      {/* Zone 2 – Scrollable body */}
      <div className="step-form-body">
        {/* Summary Card with Integration Details */}
        <div className="card">
          <div className="card-body">
            <h6 className="card-title">Integration Summary</h6>
            <Table borderless size="sm">
              <tbody>
                {summaryRows.map((row) => (
                  <tr key={row.label}>
                    <td className="text-muted" style={{ width: '40%' }}>
                      {row.label}:
                    </td>
                    <td>{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      </div>

      {/* Zone 3 – Action buttons (pinned footer) */}
      <div className="step-action-footer d-flex justify-content-between">
        {allowBack && (
          <Button color="secondary" onClick={onBack} disabled={loading}>
            Back
          </Button>
        )}
        <Button
          color="primary"
          onClick={handleComplete}
          disabled={loading}
          className={allowBack ? '' : 'ml-auto'}
        >
          {loading ? (
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <CircleSpinner size={16} color="#ffffff" loading={true} />
              <span style={{ marginLeft: '8px' }}>Creating Integration...</span>
            </span>
          ) : (
            'Integrate'
          )}
        </Button>
      </div>
    </div>
  );
};

export default ReviewStep;
